import { useState, useCallback, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Surface } from '@deckspace/types';
import type { ApiClient } from '@deckspace/client';
import type { SurfaceId, WorkspaceId } from '@deckspace/ids';
import { queryKeys } from '../lib/queryKeys';
import type { WorkspaceDetail } from '../lib/queries';

export interface SurfaceMutations {
  deleteItem(itemId: string): Promise<boolean>;
  updateSurfaceNodeProps(
    itemId: string,
    nodeId: string,
    nodeProps: Record<string, unknown>
  ): Promise<void>;
  isDeleting: boolean;
}

export function useSurfaceMutations(
  api: ApiClient,
  workspaceId: string | undefined
): SurfaceMutations {
  const queryClient = useQueryClient();
  const [isDeleting, setIsDeleting] = useState(false);
  const isDeletingRef = useRef(false);

  const deleteItem = useCallback(
    async (itemId: string): Promise<boolean> => {
      if (!workspaceId || isDeletingRef.current) return false;
      isDeletingRef.current = true;
      setIsDeleting(true);
      try {
        await api.deleteSurface(itemId as SurfaceId);
        queryClient.invalidateQueries({
          queryKey: queryKeys.workspace.detail(workspaceId as WorkspaceId),
        });
        return true;
      } catch (err) {
        console.error('[surfaceMutation] delete item', err);
        return false;
      } finally {
        isDeletingRef.current = false;
        setIsDeleting(false);
      }
    },
    [workspaceId, api, queryClient]
  );

  const updateSurfaceNodeProps = useCallback(
    async (itemId: string, nodeId: string, nodeProps: Record<string, unknown>) => {
      if (!workspaceId) return;

      const workspace = queryClient.getQueryData<WorkspaceDetail>(
        queryKeys.workspace.detail(workspaceId)
      );
      const item = workspace?.surfaces.find((i: Surface) => i.id === itemId);
      if (!item) return;

      const updatedComponents = item.components.map((n) =>
        n.id === nodeId ? { ...n, props: { ...n.props, ...nodeProps } } : n
      );

      try {
        await api.updateSurface(itemId as SurfaceId, { components: updatedComponents });
      } catch (err) {
        console.error('[surfaceMutation] update surface node', err);
      }
    },
    [workspaceId, api, queryClient]
  );

  return useMemo(
    () => ({ deleteItem, updateSurfaceNodeProps, isDeleting }),
    [deleteItem, updateSurfaceNodeProps, isDeleting]
  );
}
